import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useWorkspace } from "contexts/WorkspaceContext";
import paths from "routes/paths";
import AuthLoading from "components/common/AuthLoading";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import "./layout.css";

const WORKSPACE_QUERY_PARAM = "wid";
const LEGACY_WORKSPACE_QUERY_PARAM = "workspaceId";
const STARTER_WORKSPACE_DRAFT_KEY = "olreadiaInitialWorkspaceDraft";
const LINKED_ACCOUNT_LOGIN_KEY = "olreadiaLinkedAccountLogin";

const hasStarterWorkspaceDraft = () => {
  if (typeof window === "undefined") return false;
  try {
    return Boolean(window.localStorage.getItem(STARTER_WORKSPACE_DRAFT_KEY));
  } catch {
    return false;
  }
};

const hasLinkedAccountLogin = () => {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(LINKED_ACCOUNT_LOGIN_KEY) === "1";
  } catch {
    return false;
  }
};

const clearLinkedAccountLogin = () => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(LINKED_ACCOUNT_LOGIN_KEY);
  } catch {
    // Session storage is best effort only.
  }
};

function WorkspaceUrlSync() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    workspaces,
    selectedWorkspaceId,
    selectWorkspace,
    loading,
  } = useWorkspace();

  const searchParams = new URLSearchParams(location.search);
  const urlWorkspaceId =
    searchParams.get(WORKSPACE_QUERY_PARAM) ||
    searchParams.get(LEGACY_WORKSPACE_QUERY_PARAM) ||
    "";
  const urlWorkspaceMatchesAccount = Boolean(
    urlWorkspaceId &&
      workspaces.some((account) => account?.id === urlWorkspaceId),
  );

  useEffect(() => {
    if (
      !urlWorkspaceId ||
      !urlWorkspaceMatchesAccount ||
      urlWorkspaceId === selectedWorkspaceId
    ) {
      return;
    }

    selectWorkspace(urlWorkspaceId);
  }, [
    selectedWorkspaceId,
    selectWorkspace,
    urlWorkspaceId,
    urlWorkspaceMatchesAccount,
  ]);

  useEffect(() => {
    if (loading) return;

    const nextParams = new URLSearchParams(location.search);
    const currentWorkspaceId =
      nextParams.get(WORKSPACE_QUERY_PARAM) ||
      nextParams.get(LEGACY_WORKSPACE_QUERY_PARAM) ||
      "";

    if (
      urlWorkspaceId &&
      urlWorkspaceMatchesAccount &&
      urlWorkspaceId !== selectedWorkspaceId
    ) {
      return;
    }

    if (selectedWorkspaceId) {
      if (
        currentWorkspaceId === selectedWorkspaceId &&
        nextParams.get(WORKSPACE_QUERY_PARAM) === selectedWorkspaceId &&
        !nextParams.get(LEGACY_WORKSPACE_QUERY_PARAM)
      ) {
        return;
      }
      nextParams.set(WORKSPACE_QUERY_PARAM, selectedWorkspaceId);
    } else {
      if (!currentWorkspaceId) return;
      nextParams.delete(WORKSPACE_QUERY_PARAM);
    }
    nextParams.delete(LEGACY_WORKSPACE_QUERY_PARAM);

    const nextSearch = nextParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
        hash: location.hash,
      },
      { replace: true },
    );
  }, [
    loading,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    selectedWorkspaceId,
    urlWorkspaceId,
    urlWorkspaceMatchesAccount,
  ]);

  return null;
}

function WorkspaceSetupRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  const { workspaces, selectedWorkspaceId, loading, workspaceListReady, error } = useWorkspace();

  useEffect(() => {
    if (workspaceListReady && workspaces.length > 0) {
      clearLinkedAccountLogin();
    }

    if (
      loading ||
      !workspaceListReady ||
      error ||
      selectedWorkspaceId ||
      workspaces.length > 0 ||
      hasLinkedAccountLogin()
    ) {
      return;
    }

    const setupReason = hasStarterWorkspaceDraft() ? "first-time" : "repair";

    navigate(paths.getStarted, {
      replace: true,
      state: {
        from: {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
        },
        source: "missing-workspace",
        setupReason,
      },
    });
  }, [
    loading,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    error,
    selectedWorkspaceId,
    workspaces.length,
    workspaceListReady,
  ]);

  return null;
}

export default function PMLayout({ children = null }) {
  const [mobileSbOpen, setMobileSbOpen] = useState(false);
  const {
    workspaces,
    selectedWorkspace,
    selectedWorkspaceId,
    loading,
    workspaceListReady,
    error,
  } = useWorkspace();

  useEffect(() => {
    if (mobileSbOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileSbOpen]);

  const waitingForWorkspaceVerification =
    loading ||
    !workspaceListReady ||
    (!error && selectedWorkspaceId && !selectedWorkspace) ||
    (!error && workspaceListReady && workspaces.length === 0 && !hasLinkedAccountLogin());

  if (waitingForWorkspaceVerification) {
    return (
      <>
        <WorkspaceSetupRedirect />
        <AuthLoading message="Processing..." />
      </>
    );
  }

  return (
    <div className="pm-root">
      <WorkspaceUrlSync />
      <WorkspaceSetupRedirect />
      {mobileSbOpen && (
        <div className="pm-sb-backdrop" onClick={() => setMobileSbOpen(false)} />
      )}
      <Sidebar mobileSbOpen={mobileSbOpen} onCloseMobile={() => setMobileSbOpen(false)} />
      <div className="pm-main">
        <Topbar onToggleMobile={() => setMobileSbOpen((o) => !o)} />
        <main className="pm-cnt">
          {children || <Outlet />}
        </main>
      </div>
    </div>
  );
}
